<template>
    <div 
        v-if="pagecount > 1"
        class="pagination-inline"
        data-testid="pagination"
    >
        <div class="pagination-inline__content" data-testid="pagination__row">
            <!-- Left section: Pagination controls -->
            <div class="pagination-inline__controls" data-testid="pagination__controls">
                <v-pagination 
                    v-model="page" 
                    class="pagination-inline__pagination" 
                    :length="pagecount" 
                    :total-visible="totalVisible"
                    @input="emitPaginatedData" 
                    data-testid="pagination__pagination"
                ></v-pagination>
            </div>

            <!-- Right section: Go to page + Page info -->
            <div class="pagination-inline__right" data-testid="pagination__right">
                <div class="pagination-inline__goto" data-testid="pagination__go-to-container">
                    <span class="pagination-inline__goto-label" data-testid="pagination__go-to-text">Go to</span>
                    <lb-number 
                        min="1" 
                        label="" 
                        hidedetails 
                        :max="pagecount" 
                        @input="handlePageInput"
                        @change="emitPaginatedData" 
                        v-model="page" 
                        class="pagination-inline__goto-input"
                        data-testid="pagination__go-to-input" 
                    />
                </div>
                <div class="pagination-inline__info" data-testid="pagination__info">
                    <span class="pagination-inline__info-text">
                        Page {{ page }} of {{ pagecount }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * Pagination Component
 * 
 * A reusable pagination component for table data.
 * Provides page navigation controls and "Go to page" input.
 * 
 * @example
 * <Pagination
 *   :list="filteredData"
 *   :itemsperpage="itemsPerPage"
 *   @paginated-data="setPaginatedData"
 * />
 */
export default {
    name: "Pagination",
    
    props: {
        /**
         * Array of items to paginate
         */
        list: {
            type: Array,
            default: () => [],
        },
        
        /**
         * Number of items per page
         */
        itemsperpage: {
            type: Number,
            default: 20,
        },
    },
    
    data() {
        return {
            // Current page number
            page: 1,
            // Page input value
            pageInput: null,
        };
    },
    
    computed: {
        /**
         * Calculate total number of pages
         */
        pagecount() {
            return Math.ceil(this.list.length / this.itemsperpage);
        },
        
        /**
         * Calculate total visible pages based on pagecount
         * Shows more pages when there are fewer total pages
         * Always keeps current page visible in the center
         */
        totalVisible() {
            // For smaller page counts, show all pages
            if (this.pagecount <= 7) {
                return this.pagecount;
            }
            // For larger counts, show 7 (current page centered with neighbors)
            return 7;
        },
    },
    
    watch: {
        /**
         * Emit paginated data when page changes
         */
        page() {
            this.emitPaginatedData();
        },
        
        /**
         * Adjust page when list changes
         */
        list() {
            const pageAdjusted = this.ensureValidPage();
            if (!pageAdjusted) {
                this.emitPaginatedData();
            }
        },
        
        /**
         * Adjust page when items per page changes
         */
        itemsperpage() {
            const pageAdjusted = this.ensureValidPage();
            if (!pageAdjusted) {
                this.emitPaginatedData();
            }
        }
    },
    
    methods: {
        /**
         * Ensure page number is within valid range
         * @returns {Boolean} True if page was adjusted, false otherwise
         */
        ensureValidPage() {
            const totalPages = this.pagecount;
            let targetPage;

            if (totalPages <= 0) {
                targetPage = 1;
            } else if (this.page > totalPages) {
                targetPage = totalPages;
            } else if (this.page < 1) {
                targetPage = 1;
            } else {
                targetPage = this.page;
            }

            if (targetPage !== this.page) {
                this.page = targetPage;
                return true;
            }

            return false;
        },
        
        /**
         * Calculate and emit paginated data slice
         */
        emitPaginatedData() {
            if (this.ensureValidPage()) {
                return;
            }
            const start = (this.page - 1) * this.itemsperpage;
            const end = this.page * this.itemsperpage;
            const paginatedData = this.list.slice(start, end);
            this.$emit('paginated-data', { data: paginatedData, page: this.page });  
        },
        
        /**
         * Handle manual page input
         * @param {Number} value - Page number input value
         */
        handlePageInput(value) {
            const pageValue = parseInt(value, 10);
            if (pageValue > this.pagecount) {
                this.page = this.pagecount;
            } else if (pageValue < 1) {
                this.page = 1;
            } else {
                this.page = pageValue;
            }
            this.emitPaginatedData();
        },
    },
    
    /**
     * Emit initial page data on mount
     */
    mounted() {
        this.emitPaginatedData();
    },
};
</script>

<style lang="scss" scoped>
@import './Pagination.scss';
</style>
